import React, { useEffect, useState } from "react";
import api, { checklistApi } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Settings,
  Percent,
  Save,
  Plus,
  Trash2,
  Edit2,
  ListChecks,
  CheckSquare,
  FileText,
  Hash,
  Calendar,
  List,
  Upload,
  GripVertical,
  Copy,
  Loader2,
  DollarSign,
} from "lucide-react";

const FIELD_TYPES = [
  { value: "checkbox", label: "Checkbox", icon: CheckSquare },
  { value: "text", label: "Text Input", icon: FileText },
  { value: "number", label: "Number", icon: Hash },
  { value: "dropdown", label: "Dropdown", icon: List },
  { value: "file", label: "File Upload", icon: Upload },
  { value: "date", label: "Date", icon: Calendar },
  { value: "textarea", label: "Text Area", icon: FileText },
  { value: "url", label: "URL Link", icon: FileText },
  { value: "email", label: "Email", icon: FileText },
];

const TARGETS = [
  { value: "internal", label: "Internal", description: "PM/Sales fills this checklist" },
  { value: "client_request", label: "Client Request", description: "Client fills this form" },
];

const STAGES = [
  { value: "sales", label: "Sales Stage", description: "When signing contract" },
  { value: "project_start", label: "Project Start", description: "Before project begins" },
  { value: "task", label: "Task Level", description: "For each task/service" },
  { value: "delivery", label: "Delivery", description: "Before final delivery" },
];

// Sortable Row Component for Service Pricing
const SortableServiceRow = ({ service, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: service.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow 
      ref={setNodeRef} 
      style={style} 
      className={cn("border-zinc-800", isDragging && "bg-zinc-800/80")}
    >
      <TableCell className="w-10">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 text-zinc-500 hover:text-zinc-300"
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </TableCell>
      <TableCell className="text-zinc-50 font-medium">
        {service.name || service.service_type}
        {service.description && (
          <p className="text-xs text-zinc-500 mt-1">{service.description}</p>
        )}
      </TableCell>
      <TableCell>
        <Badge className={cn(
          "text-xs",
          service.pricing_type === "per_sqft" 
            ? "bg-blue-900/30 text-blue-400 border-blue-700"
            : "bg-green-900/30 text-green-400 border-green-700"
        )}>
          {service.pricing_type === "per_sqft" ? "Per Sq Ft" : "Per Unit"}
        </Badge>
      </TableCell>
      <TableCell className="text-right font-mono text-zinc-50">
        ${service.base_price?.toFixed(2) || "0.00"}
        {service.pricing_type === "per_sqft" && <span className="text-xs text-zinc-500">/sqft</span>}
      </TableCell>
      <TableCell className="text-center text-zinc-400">
        {service.min_quantity || 1} - {service.max_quantity || 100}
      </TableCell>
      <TableCell className="text-center">
        <Badge className={cn(
          "text-xs",
          service.is_active !== false
            ? "bg-green-900/30 text-green-400 border-green-700"
            : "bg-zinc-800 text-zinc-500 border-zinc-700"
        )}>
          {service.is_active !== false ? "Active" : "Inactive"}
        </Badge>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end gap-1">
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onEdit(service)}
            className="h-8 w-8 text-zinc-400 hover:text-zinc-50"
          >
            <Edit2 className="h-4 w-4" />
          </Button>
          {service.id && (
            <Button
              size="icon"
              variant="ghost"
              onClick={() => onDelete(service.id)}
              className="h-8 w-8 text-zinc-400 hover:text-red-400"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

// Sortable Row Component for Service Types (new extended version)
const SortableServiceTypeRow = ({ type, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: type.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow 
      ref={setNodeRef} 
      style={style} 
      className={cn("border-zinc-800", isDragging && "bg-zinc-800/80")}
    >
      <TableCell className="w-10">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 text-zinc-500 hover:text-zinc-300"
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </TableCell>
      <TableCell className="text-zinc-50 font-medium">
        {type.name}
        {type.description && (
          <p className="text-xs text-zinc-500 mt-1 max-w-[200px] truncate">{type.description}</p>
        )}
        {/* Shown for every type, empty ones too: this list is where a project's
            "Materials from client" comes from, and the PM lead could not find it. */}
        {(type.required_materials || []).length > 0 ? (
          <p className="text-[11px] text-zinc-500 mt-0.5">{type.required_materials.length} materials from client</p>
        ) : (
          <p className="text-[11px] text-amber-500/80 mt-0.5">No materials from client — Edit to add</p>
        )}
      </TableCell>
      <TableCell className="text-center">
        <Badge className={cn(
          "text-xs",
          type.is_sq_ft_pricing 
            ? "bg-blue-900/30 text-blue-400 border-blue-700"
            : "bg-zinc-800 text-zinc-400 border-zinc-700"
        )}>
          {type.is_sq_ft_pricing ? "Per Sq Ft" : "Per Unit"}
        </Badge>
      </TableCell>
      <TableCell className="text-right font-mono text-emerald-400">
        {type.partner_price > 0 ? `$${type.partner_price}` : "-"}
      </TableCell>
      <TableCell className="text-right font-mono text-sky-400">
        {type.base_price > 0 ? `€${type.base_price}` : "-"}
      </TableCell>
      <TableCell className="text-center text-zinc-400 text-sm">
        {type.min_quantity ?? 0} - {type.max_quantity || 100}
      </TableCell>
      <TableCell className="text-center text-zinc-400 text-sm">
        <span className="text-orange-400">{type.free_revisions || 3}</span>
        {" / "}
        <span className="text-blue-400">{type.partner_revisions || 2}</span>
      </TableCell>
      <TableCell className="text-center">
        <Badge className={cn(
          "text-xs",
          type.is_active !== false
            ? "bg-green-900/30 text-green-400 border-green-700"
            : "bg-zinc-800 text-zinc-500 border-zinc-700"
        )}>
          {type.is_active !== false ? "Active" : "Hidden"}
        </Badge>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end gap-1">
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onEdit(type)}
            className="h-8 w-8 text-zinc-400 hover:text-zinc-50"
            data-testid={`edit-type-${type.id}`}
          >
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onDelete(type.id)}
            className="h-8 w-8 text-zinc-400 hover:text-red-400"
            data-testid={`delete-type-${type.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

const ServicesManagementPage = () => {
  // ?tab=services opens Service Types straight away — that is where a service's
  // "Required materials from client" live, and the default Checklist Templates
  // tab (the older "Client Data Request" forms) is where the PM lead looked.
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "checklists");
  
  // Margin Settings State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [marginSettings, setMarginSettings] = useState({
    id: "global_margin_settings",
    max_artist_share_percent: 35,
    warning_threshold_percent: 35,
    critical_threshold_percent: 45,
    type_coefficients: {},
  });
  
  // Service Types State (extended with new fields)
  const [serviceTypes, setServiceTypes] = useState([]);
  const [showTypeDialog, setShowTypeDialog] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeForm, setTypeForm] = useState({
    name: "",
    is_sq_ft_pricing: false,
    free_revisions: 3,
    partner_revisions: 2,
    partner_price: 0,
    base_price: 0,
    min_quantity: 0,
    max_quantity: 100,
    description: "",
    is_active: true,
    review_managers: [],
    required_materials: []
  });
  const [availableReviewers, setAvailableReviewers] = useState([]);
  // Legacy state for backwards compatibility
  const [newTypeName, setNewTypeName] = useState("");
  const [newTypeIsSqFt, setNewTypeIsSqFt] = useState(false);
  const [newTypeFreeRevisions, setNewTypeFreeRevisions] = useState(3);
  const [newTypeReviewManagers, setNewTypeReviewManagers] = useState([]);
  const [showAddType, setShowAddType] = useState(false);
  const [editTypeName, setEditTypeName] = useState("");
  const [editTypeIsSqFt, setEditTypeIsSqFt] = useState(false);
  const [editTypeFreeRevisions, setEditTypeFreeRevisions] = useState(3);
  const [editTypeReviewManagers, setEditTypeReviewManagers] = useState([]);
  
  // Service Prices State
  const [servicePrices, setServicePrices] = useState([]);
  const [showAddService, setShowAddService] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [newService, setNewService] = useState({
    name: "",
    pricing_type: "unit",
    base_price: 0,
    min_quantity: 1,
    max_quantity: 100,
    description: "",
    is_active: true
  });
  
  // VEXOS Fees State
  const [vexosFees, setVexosFees] = useState({
    base_fee: 1500,
    orbit_fee: 400,
    unit_fee_apartments: 25,
    unit_fee_villas: 100,
    fee_360: 100,
    custom_design: 1000,
    rush_deadline: 500,
    vexos_commission_percent: 5
  });
  const [savingVexosFees, setSavingVexosFees] = useState(false);
  
  // Checklist Templates State
  const [templates, setTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: "",
    description: "",
    target: "internal",  // "internal" or "client_request"
    stage: "task",
    service_types: [],
    fields: [],
    is_active: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [marginRes, typesRes, templatesRes, pricesRes, vexosRes, usersRes] = await Promise.all([
        api.get("/settings/margin"),
        api.get("/settings/service-types"),
        checklistApi.getTemplates(),
        api.get("/settings/service-prices").catch(() => ({ data: [] })),
        api.get("/settings/vexos-fees").catch(() => ({ data: {} })),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setMarginSettings(marginRes.data);
      // service-types returns array with full objects
      setServiceTypes(typesRes.data || []);
      setTemplates(templatesRes.data || []);
      setServicePrices(pricesRes.data || []);
      if (vexosRes.data && Object.keys(vexosRes.data).length > 0) {
        setVexosFees(vexosRes.data);
      }
      // Filter users who can be reviewers (admin, team, pm_lead, art_director)
      const reviewerRoles = ["admin", "team", "pm_lead", "art_director"];
      const reviewers = (usersRes.data || []).filter(u => reviewerRoles.includes(u.role));
      setAvailableReviewers(reviewers);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  // ============ MARGIN SETTINGS HANDLERS ============
  
  const saveMarginSettings = async () => {
    setSaving(true);
    try {
      await api.put("/settings/margin", marginSettings);
      toast.success("Margin settings saved");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const updateCoefficient = (type, value) => {
    setMarginSettings(prev => ({
      ...prev,
      type_coefficients: {
        ...prev.type_coefficients,
        [type]: parseFloat(value) || 1,
      },
    }));
  };

  // ============ SERVICE TYPES HANDLERS ============
  
  const resetTypeForm = () => {
    setTypeForm({
      name: "",
      is_sq_ft_pricing: false,
      free_revisions: 3,
      partner_revisions: 2,
      partner_price: 0,
      base_price: 0,
      min_quantity: 0,
      max_quantity: 100,
      description: "",
      is_active: true,
      review_managers: [],
      required_materials: []
    });
  };

  const openAddTypeDialog = () => {
    setEditingType(null);
    resetTypeForm();
    setShowTypeDialog(true);
  };

  const openEditTypeDialog = (type) => {
    setEditingType(type);
    setTypeForm({
      name: type.name || "",
      is_sq_ft_pricing: type.is_sq_ft_pricing || false,
      free_revisions: type.free_revisions || 3,
      partner_revisions: type.partner_revisions || 2,
      partner_price: type.partner_price || 0,
      base_price: type.base_price || 0,
      min_quantity: type.min_quantity ?? 0,
      max_quantity: type.max_quantity || 100,
      description: type.description || "",
      is_active: type.is_active !== false,
      review_managers: type.review_managers || [],
      required_materials: type.required_materials || []
    });
    setShowTypeDialog(true);
  };

  const saveServiceType = async () => {
    if (!typeForm.name.trim()) {
      toast.error("Name is required");
      return;
    }
    
    try {
      if (editingType) {
        // Update existing
        await api.put(`/settings/service-types/${editingType.id}`, typeForm);
        toast.success("Service type updated");
      } else {
        // Create new
        await api.post("/settings/service-types", typeForm);
        toast.success("Service type added");
      }
      setShowTypeDialog(false);
      resetTypeForm();
      setEditingType(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save service type");
    }
  };

  const addServiceType = async () => {
    if (!newTypeName.trim()) return;
    
    try {
      await api.post("/settings/service-types", { 
        name: newTypeName,
        is_sq_ft_pricing: newTypeIsSqFt,
        free_revisions: newTypeFreeRevisions,
        review_managers: newTypeReviewManagers
      });
      toast.success("Service type added");
      setNewTypeName("");
      setNewTypeIsSqFt(false);
      setNewTypeFreeRevisions(3);
      setNewTypeReviewManagers([]);
      setShowAddType(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add service type");
    }
  };

  const deleteServiceType = async (id) => {
    if (!confirm("Delete this service type?")) return;
    
    try {
      await api.delete(`/settings/service-types/${id}`);
      toast.success("Service type deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete service type");
    }
  };

  const startEditType = (type) => {
    setEditingType(type);
    setEditTypeName(type.name);
    setEditTypeIsSqFt(type.is_sq_ft_pricing || false);
    setEditTypeFreeRevisions(type.free_revisions || 3);
    setEditTypeReviewManagers(type.review_managers || []);
  };

  const saveEditType = async () => {
    if (!editingType || !editTypeName.trim()) return;
    
    try {
      await api.put(`/settings/service-types/${editingType.id}`, {
        name: editTypeName,
        is_sq_ft_pricing: editTypeIsSqFt,
        free_revisions: editTypeFreeRevisions,
        review_managers: editTypeReviewManagers
      });
      toast.success("Service type updated");
      setEditingType(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update service type");
    }
  };

  const handleServiceTypeDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    
    const oldIndex = serviceTypes.findIndex(t => t.id === active.id);
    const newIndex = serviceTypes.findIndex(t => t.id === over.id);
    
    const newOrder = arrayMove(serviceTypes, oldIndex, newIndex);
    setServiceTypes(newOrder);
    
    try {
      await api.put("/settings/service-types/reorder", {
        order: newOrder.map(t => t.id)
      });
    } catch (error) {
      console.error("Failed to save order", error);
      fetchData();
    }
  };

  const toggleTypeFormReviewManager = (managerId) => {
    setTypeForm(prev => ({
      ...prev,
      review_managers: prev.review_managers.includes(managerId)
        ? prev.review_managers.filter(id => id !== managerId)
        : [...prev.review_managers, managerId]
    }));
  };

  const toggleReviewManager = (managerId, isNew = false) => {
    if (isNew) {
      setNewTypeReviewManagers(prev => 
        prev.includes(managerId) 
          ? prev.filter(id => id !== managerId)
          : [...prev, managerId]
      );
    } else {
      setEditTypeReviewManagers(prev => 
        prev.includes(managerId) 
          ? prev.filter(id => id !== managerId)
          : [...prev, managerId]
      );
    }
  };

  // ============ CHECKLIST TEMPLATE HANDLERS ============
  
  const openNewTemplate = () => {
    setEditingTemplate(null);
    setNewTemplate({
      name: "",
      description: "",
      target: "internal",
      stage: "task",
      service_types: [],
      fields: [],
      is_active: true,
    });
    setTemplateDialogOpen(true);
  };

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setNewTemplate({
      name: template.name,
      description: template.description || "",
      target: template.target || "internal",
      stage: template.stage,
      service_types: template.service_types || [],
      fields: template.fields || [],
      is_active: template.is_active,
    });
    setTemplateDialogOpen(true);
  };

  const saveTemplate = async () => {
    if (!newTemplate.name.trim()) {
      toast.error("Template name is required");
      return;
    }

    setSaving(true);
    try {
      if (editingTemplate) {
        await checklistApi.updateTemplate(editingTemplate.id, newTemplate);
        toast.success("Template updated");
      } else {
        await checklistApi.createTemplate(newTemplate);
        toast.success("Template created");
      }
      setTemplateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (id) => {
    if (!confirm("Delete this checklist template?")) return;
    
    try {
      await checklistApi.deleteTemplate(id);
      toast.success("Template deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  const duplicateTemplate = async (template) => {
    const copy = {
      name: `${template.name} (Copy)`,
      description: template.description,
      stage: template.stage,
      service_types: [...(template.service_types || [])],
      fields: template.fields.map(f => ({ ...f, id: undefined })),
      is_active: true,
    };
    
    try {
      await checklistApi.createTemplate(copy);
      toast.success("Template duplicated");
      fetchData();
    } catch (error) {
      toast.error("Failed to duplicate template");
    }
  };

  // ============ TEMPLATE FIELD HANDLERS ============
  
  const addField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      label: "",
      field_type: "checkbox",
      is_required: true,
      default_value: null,
      options: null,
      placeholder: "",
      help_text: "",
      order: newTemplate.fields.length,
    };
    setNewTemplate(prev => ({
      ...prev,
      fields: [...prev.fields, newField],
    }));
  };

  const updateField = (index, updates) => {
    setNewTemplate(prev => ({
      ...prev,
      fields: prev.fields.map((f, i) => i === index ? { ...f, ...updates } : f),
    }));
  };

  const removeField = (index) => {
    setNewTemplate(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index),
    }));
  };

  const moveField = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newTemplate.fields.length) return;
    
    const fields = [...newTemplate.fields];
    [fields[index], fields[newIndex]] = [fields[newIndex], fields[index]];
    setNewTemplate(prev => ({ ...prev, fields }));
  };

  // ============ DRAG & DROP SENSORS ============
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = servicePrices.findIndex((item) => item.id === active.id);
      const newIndex = servicePrices.findIndex((item) => item.id === over.id);
      
      const newOrder = arrayMove(servicePrices, oldIndex, newIndex);
      setServicePrices(newOrder);
      
      // Save new order to backend
      try {
        await api.put("/settings/service-prices/reorder", {
          order: newOrder.map(s => s.id)
        });
        toast.success("Order updated");
      } catch (error) {
        toast.error("Failed to save order");
        fetchData(); // Revert on error
      }
    }
  };

  // ============ SERVICE PRICING HANDLERS ============
  
  const saveServicePrices = async () => {
    setSaving(true);
    try {
      await api.put("/settings/service-prices", servicePrices);
      toast.success("Service prices saved");
    } catch (error) {
      toast.error("Failed to save prices");
    } finally {
      setSaving(false);
    }
  };
  
  const updateServicePrice = (serviceType, price) => {
    setServicePrices(prev => {
      const existing = prev.find(p => p.service_type === serviceType);
      if (existing) {
        return prev.map(p => p.service_type === serviceType ? { ...p, base_price: price } : p);
      }
      return [...prev, { service_type: serviceType, base_price: price }];
    });
  };
  
  const getServicePrice = (serviceType) => {
    const price = servicePrices.find(p => p.service_type === serviceType);
    return price?.base_price || 0;
  };

  const openAddService = () => {
    setEditingService(null);
    setNewService({
      name: "",
      pricing_type: "unit",
      base_price: 0,
      min_quantity: 1,
      max_quantity: 100,
      description: "",
      is_active: true
    });
    setShowAddService(true);
  };

  const openEditService = (service) => {
    setEditingService(service);
    setNewService({
      name: service.name || service.service_type || "",
      pricing_type: service.pricing_type || "unit",
      base_price: service.base_price || 0,
      min_quantity: service.min_quantity || 1,
      max_quantity: service.max_quantity || 100,
      description: service.description || "",
      is_active: service.is_active !== false
    });
    setShowAddService(true);
  };

  const saveService = async () => {
    if (!newService.name.trim()) {
      toast.error("Service name is required");
      return;
    }
    
    setSaving(true);
    try {
      if (editingService?.id) {
        await api.put(`/settings/service-prices/${editingService.id}`, newService);
        toast.success("Service updated");
      } else {
        await api.post("/settings/service-prices", newService);
        toast.success("Service created");
      }
      setShowAddService(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save service");
    } finally {
      setSaving(false);
    }
  };

  const deleteService = async (serviceId) => {
    if (!confirm("Delete this service?")) return;
    
    try {
      await api.delete(`/settings/service-prices/${serviceId}`);
      toast.success("Service deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete service");
    }
  };

  const saveVexosFees = async () => {
    setSavingVexosFees(true);
    try {
      await api.put("/settings/vexos-fees", vexosFees);
      toast.success("VEXOS fees saved");
    } catch (error) {
      toast.error("Failed to save VEXOS fees");
    } finally {
      setSavingVexosFees(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope']">Services Management</h1>
          <p className="text-zinc-400 text-sm">Manage service types, margins, and checklist templates</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="checklists" className="data-[state=active]:bg-orange-500">
            <ListChecks className="h-4 w-4 mr-2" />
            Checklist Templates
          </TabsTrigger>
          <TabsTrigger value="services" className="data-[state=active]:bg-orange-500">
            <Settings className="h-4 w-4 mr-2" />
            Service Types
          </TabsTrigger>
          <TabsTrigger value="pricing" className="data-[state=active]:bg-orange-500">
            <DollarSign className="h-4 w-4 mr-2" />
            Service Pricing
          </TabsTrigger>
          <TabsTrigger value="vexos" className="data-[state=active]:bg-orange-500">
            <DollarSign className="h-4 w-4 mr-2" />
            VEXOS Fees
          </TabsTrigger>
          <TabsTrigger value="margins" className="data-[state=active]:bg-orange-500">
            <Percent className="h-4 w-4 mr-2" />
            Margin Settings
          </TabsTrigger>
        </TabsList>

        {/* ============ CHECKLIST TEMPLATES TAB ============ */}
        <TabsContent value="checklists" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-zinc-400 text-sm">Create and manage reusable checklist templates</p>
            <Button onClick={openNewTemplate} className="bg-orange-500 hover:bg-orange-600">
              <Plus className="h-4 w-4 mr-2" />
              New Template
            </Button>
          </div>

          {/* Templates Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template) => (
              <Card key={template.id} className="bg-zinc-900/50 border-zinc-800">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-zinc-50 text-lg">{template.name}</CardTitle>
                      <div className="flex gap-1 mt-1">
                        <Badge variant="outline" className={cn(
                          "text-xs",
                          template.target === "client_request" 
                            ? "bg-cyan-500/10 text-cyan-400 border-cyan-500/30"
                            : "bg-zinc-800 text-zinc-400 border-zinc-700"
                        )}>
                          {template.target === "client_request" ? "Client Request" : "Internal"}
                        </Badge>
                        <Badge variant="outline" className={cn(
                          "text-xs",
                          template.stage === "sales" ? "bg-blue-500/10 text-blue-400 border-blue-500/30" :
                          template.stage === "project_start" ? "bg-green-500/10 text-green-400 border-green-500/30" :
                          template.stage === "task" ? "bg-orange-500/10 text-orange-400 border-orange-500/30" :
                          "bg-purple-500/10 text-purple-400 border-purple-500/30"
                        )}>
                          {STAGES.find(s => s.value === template.stage)?.label || template.stage}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => duplicateTemplate(template)}
                        className="h-8 w-8 text-zinc-400 hover:text-zinc-50"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => openEditTemplate(template)}
                        className="h-8 w-8 text-zinc-400 hover:text-zinc-50"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => deleteTemplate(template.id)}
                        className="h-8 w-8 text-zinc-400 hover:text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {template.description && (
                    <p className="text-zinc-500 text-sm mb-3">{template.description}</p>
                  )}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-zinc-400">
                      {template.fields?.length || 0} fields
                    </span>
                    {template.service_types?.length > 0 && (
                      <span className="text-zinc-500 text-xs">
                        {template.service_types.length} service type(s)
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}

            {templates.length === 0 && (
              <div className="col-span-full text-center py-12 text-zinc-500">
                <ListChecks className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No checklist templates yet</p>
                <p className="text-sm mt-1">Create your first template to get started</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* ============ SERVICE TYPES TAB (Extended) ============ */}
        <TabsContent value="services" className="space-y-4">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-zinc-50">Service Types</CardTitle>
                  <p className="text-sm text-zinc-400 mt-1">Manage service types with pricing and revisions. Drag to reorder.</p>
                  <p className="text-xs text-zinc-500 mt-1">“Materials from client” on a project come from here: Edit a service → <span className="text-zinc-300">Required materials from client</span>.</p>
                </div>
                <Button size="sm" onClick={openAddTypeDialog} className="bg-orange-500 hover:bg-orange-600" data-testid="add-service-type-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add Type
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleServiceTypeDragEnd}>
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800">
                      <TableHead className="text-zinc-400 w-10"></TableHead>
                      <TableHead className="text-zinc-400">Name</TableHead>
                      <TableHead className="text-zinc-400 text-center">Pricing</TableHead>
                      <TableHead className="text-zinc-400 text-right">Partner $</TableHead>
                      <TableHead className="text-zinc-400 text-right">Base €</TableHead>
                      <TableHead className="text-zinc-400 text-center">Qty Range</TableHead>
                      <TableHead className="text-zinc-400 text-center">Revisions</TableHead>
                      <TableHead className="text-zinc-400 text-center">Status</TableHead>
                      <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <SortableContext items={serviceTypes.map(t => t.id)} strategy={verticalListSortingStrategy}>
                      {serviceTypes.map((type) => (
                        <SortableServiceTypeRow key={type.id} type={type} onEdit={openEditTypeDialog} onDelete={deleteServiceType} />
                      ))}
                    </SortableContext>
                  </TableBody>
                </Table>
              </DndContext>
              {serviceTypes.length === 0 && <p className="text-center py-8 text-zinc-500">No service types. Click "Add Type" to create.</p>}
              <div className="mt-4 pt-4 border-t border-zinc-800 flex items-center gap-6 text-xs text-zinc-500">
                <span>Revisions: <span className="text-orange-400">Artists</span> / <span className="text-blue-400">Partners</span></span>
              </div>
            </CardContent>
          </Card>

          <Dialog open={showTypeDialog} onOpenChange={setShowTypeDialog}>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-zinc-50">{editingType ? "Edit Service Type" : "Add Service Type"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                <div>
                  <Label className="text-zinc-300">Name *</Label>
                  <Input value={typeForm.name} onChange={(e) => setTypeForm({...typeForm, name: e.target.value})} placeholder="e.g., Interior Render" className="bg-zinc-950 border-zinc-800 text-zinc-50 mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Partner Price ($)</Label>
                    <Input type="number" value={typeForm.partner_price} onChange={(e) => setTypeForm({...typeForm, partner_price: parseFloat(e.target.value) || 0})} className="bg-zinc-950 border-zinc-800 text-zinc-50 mt-1" />
                    <p className="text-xs text-zinc-500 mt-1">Visible only to TRP</p>
                  </div>
                  <div>
                    <Label className="text-zinc-300">Base Price (€)</Label>
                    <Input type="number" value={typeForm.base_price} onChange={(e) => setTypeForm({...typeForm, base_price: parseFloat(e.target.value) || 0})} className="bg-zinc-950 border-zinc-800 text-zinc-50 mt-1" />
                    <p className="text-xs text-zinc-500 mt-1">EUR per {typeForm.is_sq_ft_pricing ? "m²" : "unit"} for margin calc</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Pricing Type</Label>
                    <div className="flex items-center gap-2 mt-2 h-9">
                      <Switch checked={typeForm.is_sq_ft_pricing} onCheckedChange={(v) => setTypeForm({...typeForm, is_sq_ft_pricing: v})} />
                      <span className="text-zinc-400 text-sm">{typeForm.is_sq_ft_pricing ? "Per m² / sq ft" : "Per unit"}</span>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Min Quantity</Label>
                    <Input type="number" min="0" value={typeForm.min_quantity} onChange={(e) => setTypeForm({...typeForm, min_quantity: parseInt(e.target.value) || 0})} className="bg-zinc-950 border-zinc-800 text-zinc-50 mt-1" />
                  </div>
                  <div>
                    <Label className="text-zinc-300">Max Quantity</Label>
                    <Input type="number" min="1" value={typeForm.max_quantity} onChange={(e) => setTypeForm({...typeForm, max_quantity: parseInt(e.target.value) || 100})} className="bg-zinc-950 border-zinc-800 text-zinc-50 mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Free Revisions (Artists)</Label>
                    <Input type="number" min="0" max="20" value={typeForm.free_revisions} onChange={(e) => setTypeForm({...typeForm, free_revisions: parseInt(e.target.value) || 0})} className="bg-zinc-950 border-zinc-800 text-zinc-50 mt-1" />
                  </div>
                  <div>
                    <Label className="text-zinc-300">Partner Revisions</Label>
                    <Input type="number" min="0" max="20" value={typeForm.partner_revisions} onChange={(e) => setTypeForm({...typeForm, partner_revisions: parseInt(e.target.value) || 0})} className="bg-zinc-950 border-zinc-800 text-zinc-50 mt-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-zinc-300">Description</Label>
                  <Textarea value={typeForm.description} onChange={(e) => setTypeForm({...typeForm, description: e.target.value})} placeholder="Optional..." className="bg-zinc-950 border-zinc-800 text-zinc-50 mt-1" rows={2} />
                </div>
                {/* What the client hands over for this service. Attached to a
                    project the moment the service is put into its scope; the PM
                    ticks items off and the client sees the same list. */}
                <div>
                  <Label className="text-zinc-300">Required materials from client</Label>
                  <Textarea
                    value={(typeForm.required_materials || []).join("\n")}
                    onChange={(e) => setTypeForm({ ...typeForm, required_materials: e.target.value.split("\n") })}
                    placeholder={"One per line, e.g.\n3D model of the Project\nMaster plan of the object"}
                    className="bg-zinc-950 border-zinc-800 text-zinc-50 mt-1 text-sm"
                    rows={6}
                    data-testid="service-type-materials"
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    One per line. Added to a project when this service goes into its scope; changing the list doesn't touch projects that already have it.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={typeForm.is_active} onCheckedChange={(v) => setTypeForm({...typeForm, is_active: v})} />
                  <div>
                    <Label className="text-zinc-300">Active</Label>
                    <p className="text-xs text-zinc-500">Hidden types don't appear in dropdowns</p>
                  </div>
                </div>
                <div>
                  <Label className="text-zinc-300 text-sm">Review Managers</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {availableReviewers.map(r => (
                      <Badge key={r.id} variant="outline" className={cn("cursor-pointer transition-colors", typeForm.review_managers.includes(r.id) ? "bg-orange-500/20 text-orange-400 border-orange-500" : "bg-zinc-800 text-zinc-400 border-zinc-700 hover:border-orange-500")} onClick={() => toggleTypeFormReviewManager(r.id)}>
                        {r.name || r.email} {typeForm.review_managers.includes(r.id) && "✓"}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowTypeDialog(false)} className="border-zinc-700">Cancel</Button>
                <Button onClick={saveServiceType} className="bg-orange-500 hover:bg-orange-600">{editingType ? "Save Changes" : "Add Type"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ============ SERVICE PRICING TAB ============ */}
        <TabsContent value="pricing" className="space-y-4">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-zinc-50">Service Pricing</CardTitle>
                  <p className="text-sm text-zinc-400 mt-1">Manage services with pricing types and quantity ranges</p>
                </div>
                <Button onClick={openAddService} className="bg-orange-500 hover:bg-orange-600">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Service
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800">
                      <TableHead className="text-zinc-400 w-10"></TableHead>
                      <TableHead className="text-zinc-400">Service Name</TableHead>
                      <TableHead className="text-zinc-400">Type</TableHead>
                      <TableHead className="text-zinc-400 text-right">Base Price (€)</TableHead>
                      <TableHead className="text-zinc-400 text-center">Quantity Range</TableHead>
                      <TableHead className="text-zinc-400 text-center">Status</TableHead>
                      <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <SortableContext
                      items={servicePrices.map(s => s.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {servicePrices.map((service) => (
                        <SortableServiceRow
                          key={service.id}
                          service={service}
                          onEdit={openEditService}
                          onDelete={deleteService}
                        />
                      ))}
                    </SortableContext>
                  </TableBody>
                </Table>
              </DndContext>
              {servicePrices.length === 0 && (
                <p className="text-sm text-zinc-500 text-center py-8">
                  No services configured. Click "Add Service" to create your first service.
                </p>
              )}
            </CardContent>
          </Card>
          
          <div className="text-sm text-zinc-500 bg-zinc-900/30 p-4 rounded-lg border border-zinc-800">
            <p className="font-medium text-zinc-400 mb-2">Pricing Types:</p>
            <ul className="list-disc list-inside space-y-1">
              <li><span className="text-blue-400">Per Sq Ft</span> — price is calculated by square footage (e.g., floor plans, renders)</li>
              <li><span className="text-green-400">Per Unit</span> — fixed price per item (e.g., single render, 360 tour)</li>
              <li>Quantity range defines min/max items that can be ordered at once</li>
              <li>Drag rows using the grip handle to reorder</li>
            </ul>
          </div>
        </TabsContent>

        {/* Add/Edit Service Dialog */}
        <Dialog open={showAddService} onOpenChange={setShowAddService}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-zinc-50">
                {editingService ? "Edit Service" : "Add New Service"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Service Name *</Label>
                <Input
                  value={newService.name}
                  onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                  placeholder="e.g., Interior Render"
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300">Pricing Type</Label>
                <Select
                  value={newService.pricing_type}
                  onValueChange={(v) => setNewService({ ...newService, pricing_type: v })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="unit" className="text-zinc-50">Per Unit (fixed price)</SelectItem>
                    <SelectItem value="per_sqft" className="text-zinc-50">Per Square Foot</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300">
                  Base Price ($){newService.pricing_type === "per_sqft" && " per sq ft"}
                </Label>
                <Input
                  type="number"
                  min="0"
                  step={newService.pricing_type === "per_sqft" ? "0.01" : "1"}
                  value={newService.base_price}
                  onChange={(e) => setNewService({ ...newService, base_price: parseFloat(e.target.value) || 0 })}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Min Quantity</Label>
                  <Input
                    type="number"
                    min="1"
                    value={newService.min_quantity}
                    onChange={(e) => setNewService({ ...newService, min_quantity: parseInt(e.target.value) || 1 })}
                    className="bg-zinc-800 border-zinc-700 text-zinc-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Max Quantity</Label>
                  <Input
                    type="number"
                    min="1"
                    value={newService.max_quantity}
                    onChange={(e) => setNewService({ ...newService, max_quantity: parseInt(e.target.value) || 100 })}
                    className="bg-zinc-800 border-zinc-700 text-zinc-50"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300">Description (optional)</Label>
                <Textarea
                  value={newService.description}
                  onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                  placeholder="Brief description of the service..."
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                  rows={2}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label className="text-zinc-300">Active</Label>
                <Switch
                  checked={newService.is_active}
                  onCheckedChange={(checked) => setNewService({ ...newService, is_active: checked })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddService(false)} className="border-zinc-700 text-zinc-300">
                Cancel
              </Button>
              <Button onClick={saveService} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                {editingService ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ============ VEXOS FEES TAB ============ */}
        <TabsContent value="vexos" className="space-y-4">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-zinc-50">VEXOS Fee Structure</CardTitle>
                  <p className="text-sm text-zinc-400 mt-1">Configure pricing for VEXOS software projects</p>
                </div>
                <Button 
                  onClick={saveVexosFees}
                  disabled={savingVexosFees}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  {savingVexosFees ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Fees
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Base Fee (Setup)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                    <Input
                      type="number"
                      value={vexosFees.base_fee}
                      onChange={(e) => setVexosFees({...vexosFees, base_fee: parseFloat(e.target.value) || 0})}
                      className="bg-zinc-800 border-zinc-700 text-zinc-50 pl-7"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Orbit Fee</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                    <Input
                      type="number"
                      value={vexosFees.orbit_fee}
                      onChange={(e) => setVexosFees({...vexosFees, orbit_fee: parseFloat(e.target.value) || 0})}
                      className="bg-zinc-800 border-zinc-700 text-zinc-50 pl-7"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Unit Fee - Apartments</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                    <Input
                      type="number"
                      value={vexosFees.unit_fee_apartments}
                      onChange={(e) => setVexosFees({...vexosFees, unit_fee_apartments: parseFloat(e.target.value) || 0})}
                      className="bg-zinc-800 border-zinc-700 text-zinc-50 pl-7"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Unit Fee - Villas</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                    <Input
                      type="number"
                      value={vexosFees.unit_fee_villas}
                      onChange={(e) => setVexosFees({...vexosFees, unit_fee_villas: parseFloat(e.target.value) || 0})}
                      className="bg-zinc-800 border-zinc-700 text-zinc-50 pl-7"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">360° Fee</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                    <Input
                      type="number"
                      value={vexosFees.fee_360}
                      onChange={(e) => setVexosFees({...vexosFees, fee_360: parseFloat(e.target.value) || 0})}
                      className="bg-zinc-800 border-zinc-700 text-zinc-50 pl-7"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Custom Design (non-template)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                    <Input
                      type="number"
                      value={vexosFees.custom_design}
                      onChange={(e) => setVexosFees({...vexosFees, custom_design: parseFloat(e.target.value) || 0})}
                      className="bg-zinc-800 border-zinc-700 text-zinc-50 pl-7"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Rush Deadline Fee</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                    <Input
                      type="number"
                      value={vexosFees.rush_deadline}
                      onChange={(e) => setVexosFees({...vexosFees, rush_deadline: parseFloat(e.target.value) || 0})}
                      className="bg-zinc-800 border-zinc-700 text-zinc-50 pl-7"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">VEXOS Commission (%)</Label>
                  <div className="relative">
                    <Input
                      type="number"
                      step="0.1"
                      value={vexosFees.vexos_commission_percent}
                      onChange={(e) => setVexosFees({...vexosFees, vexos_commission_percent: parseFloat(e.target.value) || 0})}
                      className="bg-zinc-800 border-zinc-700 text-zinc-50 pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400">%</span>
                  </div>
                  <p className="text-xs text-zinc-500">% of client's total invoice</p>
                </div>
              </div>
              
              <div className="p-4 bg-zinc-800/50 rounded-lg">
                <h4 className="text-sm font-medium text-zinc-300 mb-2">How VEXOS Pricing Works</h4>
                <ul className="text-xs text-zinc-500 space-y-1">
                  <li>• TRP enters quantities for each component when creating a VEXOS task</li>
                  <li>• Total = (Base Fee × qty) + (Orbit × qty) + (Apartments × qty) + ...</li>
                  <li>• They also enter the total client invoice amount</li>
                  <li>• Commission = Client Invoice × {vexosFees.vexos_commission_percent}%</li>
                  <li>• Final VEXOS revenue = Component Total + Commission</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============ MARGIN SETTINGS TAB ============ */}
        <TabsContent value="margins" className="space-y-4">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-zinc-50">Global Margin Thresholds</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Max Artist Share (%)</Label>
                  <Input
                    type="number"
                    value={marginSettings.max_artist_share_percent}
                    onChange={(e) => setMarginSettings(prev => ({
                      ...prev,
                      max_artist_share_percent: parseFloat(e.target.value) || 0,
                    }))}
                    className="bg-zinc-800 border-zinc-700 text-zinc-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Warning Threshold (%)</Label>
                  <Input
                    type="number"
                    value={marginSettings.warning_threshold_percent}
                    onChange={(e) => setMarginSettings(prev => ({
                      ...prev,
                      warning_threshold_percent: parseFloat(e.target.value) || 0,
                    }))}
                    className="bg-zinc-800 border-zinc-700 text-zinc-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Critical Threshold (%)</Label>
                  <Input
                    type="number"
                    value={marginSettings.critical_threshold_percent}
                    onChange={(e) => setMarginSettings(prev => ({
                      ...prev,
                      critical_threshold_percent: parseFloat(e.target.value) || 0,
                    }))}
                    className="bg-zinc-800 border-zinc-700 text-zinc-50"
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-zinc-800">
                <Label className="text-zinc-300 mb-3 block">Type Coefficients</Label>
                <div className="space-y-2">
                  {serviceTypes.map((type) => (
                    <div key={type.id} className="flex items-center gap-4">
                      <span className="text-zinc-400 w-48">{type.name}</span>
                      <Input
                        type="number"
                        step="0.1"
                        value={marginSettings.type_coefficients?.[type.name] || 1}
                        onChange={(e) => updateCoefficient(type.name, e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-zinc-50 w-24"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button
                  onClick={saveMarginSettings}
                  disabled={saving}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ============ TEMPLATE EDITOR DIALOG ============ */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">
              {editingTemplate ? "Edit Checklist Template" : "Create Checklist Template"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Target Selection */}
            <div className="flex gap-2 p-3 bg-zinc-800/50 rounded-lg">
              {TARGETS.map((t) => (
                <Button
                  key={t.value}
                  variant="outline"
                  onClick={() => setNewTemplate(prev => ({ ...prev, target: t.value }))}
                  className={cn(
                    "flex-1 h-auto py-3 flex flex-col items-center gap-1",
                    newTemplate.target === t.value
                      ? t.value === "client_request"
                        ? "bg-cyan-500/20 border-cyan-500 text-cyan-400"
                        : "bg-orange-500/20 border-orange-500 text-orange-400"
                      : "border-zinc-700 text-zinc-400"
                  )}
                >
                  <span className="font-medium">{t.label}</span>
                  <span className="text-xs opacity-70">{t.description}</span>
                </Button>
              ))}
            </div>

            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Template Name *</Label>
                <Input
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., SPINHOUSE Checklist"
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Stage *</Label>
                <Select
                  value={newTemplate.stage}
                  onValueChange={(v) => setNewTemplate(prev => ({ ...prev, stage: v }))}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {STAGES.map((s) => (
                      <SelectItem key={s.value} value={s.value} className="text-zinc-300">
                        {s.label} - {s.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300">Description</Label>
              <Textarea
                value={newTemplate.description}
                onChange={(e) => setNewTemplate(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe what this checklist is for..."
                className="bg-zinc-800 border-zinc-700 text-zinc-50 min-h-[60px]"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300">Apply to Service Types (leave empty for all)</Label>
              <div className="flex flex-wrap gap-2 p-3 bg-zinc-800/50 rounded-lg min-h-[48px]">
                {serviceTypes.map((type) => (
                  <Badge
                    key={type.id}
                    variant="outline"
                    className={cn(
                      "cursor-pointer transition-colors",
                      newTemplate.service_types.includes(type.name)
                        ? "bg-orange-500/20 text-orange-400 border-orange-500/50"
                        : "bg-zinc-800 text-zinc-400 border-zinc-700 hover:border-orange-500/30"
                    )}
                    onClick={() => {
                      const types = newTemplate.service_types.includes(type.name)
                        ? newTemplate.service_types.filter(t => t !== type.name)
                        : [...newTemplate.service_types, type.name];
                      setNewTemplate(prev => ({ ...prev, service_types: types }));
                    }}
                  >
                    {type.name}
                  </Badge>
                ))}
                {serviceTypes.length === 0 && (
                  <span className="text-zinc-500 text-sm">No service types defined</span>
                )}
              </div>
            </div>

            {/* Fields */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-zinc-300">Checklist Fields</Label>
                <Button size="sm" onClick={addField} className="bg-orange-500 hover:bg-orange-600">
                  <Plus className="h-4 w-4 mr-1" />
                  Add Field
                </Button>
              </div>

              <div className="space-y-3">
                {newTemplate.fields.map((field, index) => (
                  <div
                    key={field.id}
                    className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700 space-y-3"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex flex-col gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => moveField(index, -1)}
                          disabled={index === 0}
                          className="h-6 w-6 text-zinc-500"
                        >
                          ▲
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => moveField(index, 1)}
                          disabled={index === newTemplate.fields.length - 1}
                          className="h-6 w-6 text-zinc-500"
                        >
                          ▼
                        </Button>
                      </div>

                      <div className="flex-1 grid grid-cols-3 gap-3">
                        <div className="col-span-2 space-y-1">
                          <Label className="text-xs text-zinc-500">Label *</Label>
                          <Input
                            value={field.label}
                            onChange={(e) => updateField(index, { label: e.target.value })}
                            placeholder="Field label"
                            className="bg-zinc-900 border-zinc-700 text-zinc-50"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-zinc-500">Type</Label>
                          <Select
                            value={field.field_type}
                            onValueChange={(v) => updateField(index, { field_type: v })}
                          >
                            <SelectTrigger className="bg-zinc-900 border-zinc-700 text-zinc-50">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                              {FIELD_TYPES.map((t) => (
                                <SelectItem key={t.value} value={t.value} className="text-zinc-300">
                                  {t.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={field.is_required}
                            onCheckedChange={(v) => updateField(index, { is_required: v })}
                          />
                          <Label className="text-xs text-zinc-500">Required</Label>
                        </div>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => removeField(index)}
                          className="h-8 w-8 text-zinc-500 hover:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Dropdown Options */}
                    {field.field_type === "dropdown" && (
                      <div className="ml-10 space-y-2">
                        <Label className="text-xs text-zinc-500">Options (comma-separated)</Label>
                        <Input
                          value={field.options?.map(o => o.label).join(", ") || ""}
                          onChange={(e) => {
                            const opts = e.target.value.split(",").map(s => s.trim()).filter(Boolean);
                            updateField(index, {
                              options: opts.map(o => ({ value: o.toLowerCase().replace(/\s+/g, "_"), label: o }))
                            });
                          }}
                          placeholder="Option 1, Option 2, Option 3"
                          className="bg-zinc-900 border-zinc-700 text-zinc-50"
                        />
                      </div>
                    )}

                    {/* Help Text */}
                    <div className="ml-10">
                      <Input
                        value={field.help_text || ""}
                        onChange={(e) => updateField(index, { help_text: e.target.value })}
                        placeholder="Help text (optional)"
                        className="bg-zinc-900 border-zinc-700 text-zinc-50 text-sm"
                      />
                    </div>
                  </div>
                ))}

                {newTemplate.fields.length === 0 && (
                  <div className="text-center py-8 text-zinc-500 border border-dashed border-zinc-700 rounded-lg">
                    <ListChecks className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No fields yet. Add fields to build your checklist.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setTemplateDialogOpen(false)}
              className="border-zinc-700"
            >
              Cancel
            </Button>
            <Button
              onClick={saveTemplate}
              disabled={saving}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              {editingTemplate ? "Update Template" : "Create Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServicesManagementPage;
